<template>
  <b-container
    fluid
    :class="$style.container"
  >
    <CrossPlatformBanner :slug="slug" />
    <ShopBanner :class="$style.banner" />
    <!-- Wrapper for the navigation elements -->
    <header
      ref="sticky"
      :class="$style.navigationWrapper"
    >
      <b-alert
        v-if="shopClosed"
        show
        :class="$style.closedBanner"
      >
        The shop is currently closed. Please feel free to look around, and we'll be open {{shopClosedMsg}}!
      </b-alert>
      <template v-if="!isSmallScreen()">
        <!-- Shoping bag icon -->
        <span :class="$style.glyphContainer">
          <router-link
            :class="$style.navLogoWrapper"
            :to="{ name: 'PublicSiteHome', props: { site: slug } }"
          >
            <img
              ref="glyph"
              src="@assets/images/soc-logo-02.png"
              alt="logo"
              :class="$style.glyph"
            />
          </router-link>
        </span>
        <div
          ref="siteName"
          :class="$style.title"
        >
          <NavBarTitle />
        </div>
        <!-- Account options component -->
        <div :class="$style.options">
          <AccountOptions />
        </div>
        <!-- List of navigation options -->
        <div
          ref="routesWrapper"
          :class="$style.routesWrapper"
        >
          <SecondaryNavBar
            ref="secondaryNavBar"
            :site="site"
          />
        </div>
      </template>
      <template v-else>
        <MobileNav ref="mobileNav" />
      </template>
    </header>
    <!-- Slot -->
    <div
      v-if="loading === true"
      :class="$style.slot"
    >
      <BaseIcon
        :class="$style.loadingIcon"
        name="spinner-third"
        spin
      />
    </div>
    <main
      v-else
      :class="$style.slot"
    >
      <b-row v-if="!failedLoading">
        <slot />
      </b-row>
      <div v-else>
        {{ failedText }}
      </div>
    </main>
    <!-- Footer -->
    <footer
      role="contentinfo"
      :class="$style.footer"
    >
      <b-row :class="$style.row">
        <the-footer />
      </b-row>
    </footer>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import NavBarTitle from '@components/nav-bar/nav-bar-title.vue'
import AccountOptions from '@/components/nav-bar/account-options.vue'
import SecondaryNavBar from '@/components/nav-bar/secondary-nav-bar.vue'
import MobileNav from '@/components/nav-bar/mobile-nav.vue'
import CrossPlatformBanner from '@components/nav-bar/cross-platform-banner.vue'
import ShopBanner from '@components/nav-bar/shop-banner.vue'
import StickyFill from 'stickyfilljs'
import TheFooter from '@/components/the-footer.vue'
import { authComputed } from '@state/helpers'
import { gsap } from 'gsap'
import browser from 'browser-detect'

export default {
  components: {
    AccountOptions,
    CrossPlatformBanner,
    MobileNav,
    NavBarTitle,
    SecondaryNavBar,
    ShopBanner,
    'the-footer': TheFooter,
  },

  props: {
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    failedLoading: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // primary animation duration (in pixels scrolled)
      duration1: 0,
      // secondary animation duration (in pixels scrolled)
      duration2: 0,
      navTimeline: gsap.timeline({ paused: true }),
      smallScreen: null,
    }
  },
  computed: {
    ...authComputed,
    ...mapGetters({
      items: 'shoppingCart/getShoppingCartItems',
      slug: 'sites/getSiteSlug',
      shopClosed: 'sites/getShopClosed',
      shopClosedMsg: 'sites/getShopClosedMsg',
      site: 'sites/getSite',
      bannerStatus: 'shopBanners/getBannerStatus',
    }),
    // primary animation offset (in pixels scrolled)
    offset1() {
      if (this.bannerStatus) return 80
      return 10
    },
    // secondary animation offset (in pixels scrolled)
    offset2() {
      if (this.bannerStatus) return 90
      return 20
    },
    // tertiary animation offset(in pixels scrolled)
    offset3() {
      if (this.bannerStatus) return 70
      return 0
    },
  },

  created() {
    if (this.site.shopLaunchReady === true) {
      this.fetchShoppingCart()
      this.fetchShopCategories(this.site.id)
        .then(() => {
          this.loadedSuccessfully = true
        })
        .catch(() => {
          this.loadedSuccessfully = false
        })
    }
  },

  mounted() {
    const sticky = this.$refs.sticky

    // Polyfill for older browsers, adds the `position: sticky` property to the nav wrapper
    StickyFill.addOne(sticky)
    this.smallScreen = this.isSmallScreen()

    // timeout helps offset shop banner loading
    this.$nextTick(() => {
      this.navTimeline.time(pageYOffset)
      // Register scroll handler
      window.addEventListener(
        'scroll',
        (event) => {
          this.smallScreen = this.isSmallScreen()
          this.navTimeline.time(pageYOffset)
        },
        { passive: true }
      )
      if (this.smallScreen === true) {
        const logo = this.$refs.mobileNav.$refs.glyph
        this.navTimeline.to(
          logo,
          this.duration1,
          // { transform: 'translateY(-12%) scale(0.75)' },
          { yPercent: -12, scale: 0.75 },
          this.offset1
        )
      } else {
        const logo = this.$refs.glyph
        const name = this.$refs.siteName
        const routesWrapper = this.$refs.routesWrapper
        const routes = this.$refs.secondaryNavBar.$refs.routes
        const searchBar = this.$refs.secondaryNavBar.$refs.searchBar
        const searchLink = this.$refs.secondaryNavBar.$refs.searchLink
        this.navTimeline
          .to(
            logo,
            this.duration1,
            // { transform: 'translateY(-12%) scale(0.75)' },
            { yPercent: this.shopClosed ? -30 : -40, scale: 0.75 },
            this.offset1
          )
          .to(
            name,
            this.duration1,
            // { transform: 'translateY(-161%)' },
            { yPercent: -161, autoAlpha: 0 },
            this.offset1
          )
          .to(
            routes,
            this.duration1,
            // { transform: 'translateX(60px)', marginRight: '4%' },
            { x: 60, marginRight: '4%' },
            this.offset1
          )
          .to(
            searchBar,
            this.duration2,
            // { transform: 'translateX(-200px)', autoAlpha: 0 },
            { x: -400, autoAlpha: 0 },
            this.offset1
          )
          .to(searchLink, this.duration1, { autoAlpha: 1 }, this.offset3)
          .to(
            routesWrapper,
            this.duration1,
            // { transform: 'translateY(-121%)' },
            { yPercent: -121 },
            this.offset1
          )
      }
    })
  },

  // Unregister scroll handler
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },

  methods: {
    ...mapActions({
      fetchShopCategories: 'shopCategories/fetchShopCategories',
      fetchShoppingCart: 'shoppingCart/fetchShoppingCart',
    }),
    checkIE() {
      const result = browser()
      if (result.name === 'ie') {
        return true
      }
      return false
    },
  },
}
</script>

<style>
.alert-dismissible .close {
  top: 12px !important;
}
*:focus {
  outline: none !important;
}
.form-control:focus {
  border-color: inherit;
  -webkit-box-shadow: none;
  box-shadow: none;
}
</style>
<style lang="scss" module>
@import url('https://fonts.googleapis.com/css?family=Covered+By+Your+Grace|Open+Sans:800&display=swap');
@import '@design';

.banner {
  margin-right: -15px;
  margin-left: -15px;
}
.closedBanner {
  padding: 29px 15px;
  margin: -10px -15px -2px -15px;
  color: #fff;
  text-align: center;
  background: var(--brandColor);
  border-color: var(--brandColor);
  border-radius: 0 !important;
}
.container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
.footer {
  margin-right: -15px;
  margin-left: -15px;
}
.options {
  z-index: 5000;
  display: inline-block;
  float: right;
}
.slot {
  flex: 1;
}
.title {
  position: relative;
  top: 20px;
  display: inline-block;
}

.routesWrapper {
  position: absolute;
  top: 120%;
  z-index: -1;
  background-color: white;
  // border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.navigationWrapper {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  z-index: 100;
  padding-top: 6px;
}

.navLogoWrapper {
  display: inline-block;
}

.glyphContainer {
  padding: 5px 0;
  padding-left: 15px;
  margin-left: -15px;

  // button:focus {
  //   overflow: auto !important;
  // }
}
.glyph {
  position: relative;
  top: 20px;
  z-index: 500;
  width: 40px;
}
.row {
  flex-shrink: 0;
  margin-right: 0;
  margin-left: 0;
}

.loadingIcon {
  @extend %typography-xxlarge;

  position: relative;
  top: 5rem;
  display: block;
  margin: 0 auto;

  // stylelint-disable-next-line selector-class-pattern
  &:global(.v-enter-active) {
    transition: opacity 1s;
  }
  // stylelint-disable-next-line selector-class-pattern
  &:global(.v-enter) {
    opacity: 0;
  }
}

@media screen and (min-width: 768px) {
  .slot {
    margin-top: 100px;
  }

  .routesWrapper {
    width: 100%;
  }
}
</style>
